/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const HOLIDAY_TYPES = [
  { value: "term_holiday", label: "Term holiday" },
  { value: "half_term", label: "Half-term" },
  { value: "inset_day", label: "Inset day" },
  { value: "bank_holiday_override", label: "Bank holiday override" },
  { value: "closure", label: "School closure" },
  { value: "other", label: "Other" },
];


/* =========================
   PRESENTPLUS BRANDING
   Uses the school colour from AdminManager CSS vars.
   Falls back safely if the old shell has not been updated yet.
========================= */
const BRAND = "var(--pp-brand-primary, var(--pp-primary, #4f46e5))";
const BRAND_DARK = "var(--pp-brand-dark, var(--pp-primary, #3730a3))";
const BRAND_SOFT = "var(--pp-brand-soft, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 12%, white))";
const BRAND_BORDER = "var(--pp-brand-border, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 28%, white))";
const BRAND_SHADOW = "var(--pp-brand-shadow, color-mix(in srgb, var(--pp-brand-primary, var(--pp-primary, #4f46e5)) 24%, transparent))";
const BRAND_GRADIENT = "linear-gradient(135deg, var(--pp-brand-primary, var(--pp-primary, #2563eb)), var(--pp-brand-dark, var(--pp-primary, #2563eb)))";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function niceType(value) {
  return (
    HOLIDAY_TYPES.find((x) => x.value === value)?.label ||
    String(value || "Holiday").replaceAll("_", " ")
  );
}

export default function SchoolHolidaysSection({ schoolId }) {
  const currentSchoolId = schoolId || "school_001";

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");

  const [form, setForm] = useState({
    title: "",
    startDate: todayKey(),
    endDate: todayKey(),
    type: "term_holiday",
    notes: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadHolidays() {
    try {
      const snap = await getDocs(
        query(
          collection(db, "schools", currentSchoolId, "schoolHolidays"),
          orderBy("startDate", "asc")
        )
      );

      setRows(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load school holidays.");
    }
  }

  useEffect(() => {
    loadHolidays();
  }, [currentSchoolId]);

  const upcoming = useMemo(() => {
    const today = todayKey();
    return rows.filter((r) => String(r.endDate || r.startDate) >= today);
  }, [rows]);

  async function saveHoliday(e) {
    e.preventDefault();

    if (!form.title.trim()) {
      setError("Holiday title is required.");
      return;
    }

    if (!form.startDate || !form.endDate) {
      setError("Start and end date are required.");
      return;
    }

    if (form.endDate < form.startDate) {
      setError("End date cannot be before start date.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const payload = {
        title: form.title.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        type: form.type,
        notes: form.notes.trim(),
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(
          doc(db, "schools", currentSchoolId, "schoolHolidays", editingId),
          payload
        );
        setMessage("School holiday updated.");
      } else {
        await addDoc(collection(db, "schools", currentSchoolId, "schoolHolidays"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setMessage("School holiday added.");
      }

      setEditingId("");
      setForm({
        title: "",
        startDate: todayKey(),
        endDate: todayKey(),
        type: "term_holiday",
        notes: "",
      });

      await loadHolidays();
    } catch (err) {
      console.error(err);
      setError("Failed to save school holiday.");
    } finally {
      setSaving(false);
    }
  }

  function editHoliday(row) {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      startDate: row.startDate || todayKey(),
      endDate: row.endDate || row.startDate || todayKey(),
      type: row.type || "term_holiday",
      notes: row.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeHoliday(row) {
    const ok = window.confirm(`Delete "${row.title}"?`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "schools", currentSchoolId, "schoolHolidays", row.id));
      setMessage("School holiday deleted.");
      await loadHolidays();
    } catch (err) {
      console.error(err);
      setError("Failed to delete school holiday.");
    }
  }

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <p style={styles.eyebrow}>Attendance Calendar</p>
        <h2 style={styles.title}>School Holidays</h2>
        <p style={styles.subtitle}>
          Add term holidays, half-term breaks, inset days, closures, and school-specific
          non-teaching days.
        </p>
      </section>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      <section style={styles.grid}>
        <form onSubmit={saveHoliday} style={styles.card}>
          <h3 style={styles.cardTitle}>
            {editingId ? "Edit holiday" : "Add school holiday"}
          </h3>

          <label style={styles.label}>Title</label>
          <input
            style={styles.input}
            placeholder="E.g. Summer term holiday"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />

          <div style={styles.twoCol}>
            <div>
              <label style={styles.label}>Start date</label>
              <input
                type="date"
                style={styles.input}
                value={form.startDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, startDate: e.target.value }))
                }
              />
            </div>

            <div>
              <label style={styles.label}>End date</label>
              <input
                type="date"
                style={styles.input}
                value={form.endDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, endDate: e.target.value }))
                }
              />
            </div>
          </div>

          <label style={styles.label}>Type</label>
          <select
            style={styles.input}
            value={form.type}
            onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
          >
            {HOLIDAY_TYPES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <label style={styles.label}>Notes</label>
          <textarea
            style={styles.textarea}
            placeholder="Optional internal note"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryBtn} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update holiday" : "Add holiday"}
            </button>

            {editingId ? (
              <button
                type="button"
                style={styles.softBtn}
                onClick={() => {
                  setEditingId("");
                  setForm({
                    title: "",
                    startDate: todayKey(),
                    endDate: todayKey(),
                    type: "term_holiday",
                    notes: "",
                  });
                }}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        <section style={styles.card}>
          <h3 style={styles.cardTitle}>Upcoming</h3>

          <div style={styles.statBox}>
            <strong>{upcoming.length}</strong>
            <span>upcoming holiday period(s)</span>
          </div>

          <p style={styles.help}>
            These dates can be used by attendance calendars, monthly reports, and parent
            app views so school-specific holidays are not treated as absence days.
          </p>
        </section>
      </section>

      <section style={styles.tableCard}>
        <div style={styles.tableTop}>
          <div>
            <h3 style={styles.cardTitle}>Holiday records</h3>
            <p style={styles.help}>Manual school-specific calendar entries.</p>
          </div>

          <button type="button" style={styles.softBtn} onClick={loadHolidays}>
            Refresh
          </button>
        </div>

        {rows.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Start</th>
                  <th style={styles.th}>End</th>
                  <th style={styles.th}>Notes</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <strong>{row.title}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{niceType(row.type)}</span>
                    </td>
                    <td style={styles.td}>{row.startDate}</td>
                    <td style={styles.td}>{row.endDate}</td>
                    <td style={styles.td}>{row.notes || "—"}</td>
                    <td style={styles.td}>
                      <div style={styles.rowActions}>
                        <button
                          type="button"
                          style={styles.viewBtn}
                          onClick={() => editHoliday(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={styles.deleteBtn}
                          onClick={() => removeHoliday(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.empty}>No school holidays added yet.</div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: 18,
  },
  hero: {
    background: BRAND_GRADIENT,
    color: "#fff",
    borderRadius: 28,
    padding: 28,
    boxShadow: `0 24px 60px ${BRAND_SHADOW}`,
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontWeight: 900,
    opacity: 0.8,
  },
  title: {
    margin: "8px 0 0",
    fontSize: 40,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },
  subtitle: {
    margin: "10px 0 0",
    maxWidth: 780,
    lineHeight: 1.6,
    opacity: 0.94,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(280px, 0.8fr)",
    gap: 18,
  },
  card: {
    background: "#fff",
    borderRadius: 24,
    padding: 20,
    border: "1px solid #e5ebf7",
    boxShadow: "0 18px 44px rgba(20,54,110,0.08)",
    display: "grid",
    gap: 12,
  },
  cardTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 950,
    color: "#111827",
  },
  label: {
    fontSize: 13,
    fontWeight: 900,
    color: "#334155",
  },
  input: {
    minHeight: 48,
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    padding: "0 14px",
    fontSize: 15,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    minHeight: 96,
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    padding: 14,
    fontSize: 15,
    outline: "none",
    resize: "vertical",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryBtn: {
    border: "none",
    background: BRAND_GRADIENT,
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 900,
    cursor: "pointer",
  },
  softBtn: {
    border: "1px solid #d8e1f2",
    background: "#fff",
    color: "#334155",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 900,
    cursor: "pointer",
  },
  statBox: {
    borderRadius: 20,
    padding: 18,
    background: "#f8fbff",
    border: "1px solid #e5ebf7",
    display: "grid",
    gap: 4,
  },
  help: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.55,
  },
  tableCard: {
    background: "#fff",
    borderRadius: 24,
    padding: 18,
    border: "1px solid #e5ebf7",
    boxShadow: "0 18px 44px rgba(20,54,110,0.08)",
  },
  tableTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 14,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 18,
    border: "1px solid #edf2fb",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: 14,
    background: "#f8fbff",
    color: "#64748b",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 900,
    borderBottom: "1px solid #edf2fb",
  },
  td: {
    padding: 14,
    borderBottom: "1px solid #f1f5fb",
    color: "#14213d",
    verticalAlign: "top",
  },
  badge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 900,
    background: BRAND_SOFT,
    color: BRAND_DARK,
  },
  rowActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  viewBtn: {
    border: "none",
    background: BRAND_SOFT,
    color: BRAND_DARK,
    padding: "9px 12px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  deleteBtn: {
    border: "none",
    background: "#fee4e2",
    color: "#b42318",
    padding: "9px 12px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  empty: {
    padding: 24,
    textAlign: "center",
    color: "#64748b",
  },
  success: {
    background: "#ecfdf5",
    color: "#166534",
    border: "1px solid #a7f3d0",
    padding: 14,
    borderRadius: 16,
    fontWeight: 850,
  },
  error: {
    background: "#fff1f2",
    color: "#9f1239",
    border: "1px solid #fecdd3",
    padding: 14,
    borderRadius: 16,
    fontWeight: 850,
  },
};